<template>
  <div class="stock-take-details fade-in">
    <!-- Header Section -->
    <ProductDetailsHeader
        title="Stock Take"
        icon="clipboard-check"
        :main="stockTake"
    >
      <template #header-actions>
        <button
            v-if="stockTake.status === 'pending'"
            class="btn btn-success text-uppercase"
            :class="stockTakeProducts.length === 0 ? 'disabled' : ''"
            @click="confirmDialog('Complete Stock Take', submitStockTake, 'Are you sure you want to complete this stock take')">Submit Stock Take
        </button>
      </template>
    </ProductDetailsHeader>

    <!-- Available Products Section (when status is pending) -->
    <StoreProducts
        v-if="stockTake.status === 'pending'"
        :per-page="5"
        :marked-products="stockTakeProducts"
        :hide-stock="true"
        @row-click="populateAddProduct"
    />

    <!-- Stock Take Products Section -->
    <ProductDetailsPages
        title="Stock Take"
        :loading="loading"
        :detail-products="stockTakeProducts"
        :current-page="detailsPage"
        :current-per-page="detailsPerPage"
        :current-search="detailsSearch"
        @update:page="onDetailsPageChange"
        @update:per-page="onDetailsPerPageChange"
        @update:search="onDetailsSearchChange"
        :main="stockTake"
        :additional-fields="additionalFields"
        :hide-common-fields="true"
        @edit-product="populateStockTakeEdit"
        @delete-product="deleteStockTakeProduct"
    >
      <template #footer-actions>
        <button
            v-if="stockTake.status === 'pending'"
            class="btn btn-success"
            :class="stockTakeProducts.length === 0 ? 'disabled' : ''"
            @click="confirmDialog('Complete Stock Take', submitStockTake, 'Are you sure you want to complete this stock take')">
          <i class="bi bi-check-circle me-1"></i> Submit Stock Take
        </button>
      </template>
    </ProductDetailsPages>

    <!-- Add/Edit Product Modal -->
    <Modal :open="addProductModal" @closed="addProductModal = false" dialog-size="modal-lg">
      <div class="modal-header">
        <h5 class="modal-title m-0" v-if="addProductMode === 'add'">
          <i class="bi bi-plus-circle me-2 text-primary"></i>Add Product to Stock Take
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-pencil-square me-2 text-primary"></i>Edit Stock Take Product
        </h5>
        <button @click="addProductModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <input type="hidden" v-model="stockTakeDetails.expected">
          <div class="row">
            <div class="form-group col-md-6">
              <label for="name" class="form-label fw-medium mb-2">Product Name:</label>
              <input type="text" class="form-control" :value="selectedProduct.name" disabled>
            </div>
            <div class="form-group col-md-6">
              <label for="available" class="form-label fw-medium mb-2">Available Quantity:</label>
              <div class="input-group">
                <input type="number" class="form-control" autofocus="autofocus" v-model="stockTakeDetails.available" min="0" required>
              </div>
            </div>
            <div class="form-group col-12 mt-3">
              <label for="comment" class="form-label fw-medium mb-2">Comment:</label>
              <div class="input-group">
                <span class="input-group-text"><i class="bi bi-chat-text"></i></span>
                <textarea class="form-control" placeholder="Enter optional comment" v-model="stockTakeDetails.comment" rows="2"></textarea>
              </div>
              <small class="form-text text-muted">Optional notes about this stock take entry</small>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="addProductModal = false">Cancel</button>
        <button type="button" class="btn btn-primary" v-if="addProductMode === 'add'" @click="addStockTakeProduct">
          <i class="bi bi-plus-lg me-1"></i> Add Product
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateStockTakeProduct">
          <i class="bi bi-check-lg me-1"></i> Update Product
        </button>
      </div>
    </Modal>

  </div>
</template>

<script>
import stockTakeService from "@/api/stockTakeService";
import confirmDialog  from "@/utils/confirmDialog";
import loadingButton from "@/utils/loadingButton";
import ProductDetailsHeader from "@/components/details/ProductDetailsHeader.vue";
import StoreProducts from "@/components/store/StoreProducts.vue";
import ProductDetailsPages from "@/components/details/ProductDetailsPages.vue";

export default {
  name: "StockTakeDetails",
  components: { ProductDetailsHeader, StoreProducts, ProductDetailsPages },

  data(){
    return {
      profile: {},
      store: {},
      stockTake: {
        store: {},
        user: {},
      },
      stockTakeDetails: {
        stock_take_id: this.$route.params.id,
        product_id: '',
        expected: '',
        available: '',
      },
      selectedProduct: {},
      stockTakeProducts: { data: [] },
      detailsPage: 1,
      detailsPerPage: 20,
      detailsSearch: '',
      loading: true,
      addProductModal: false,
      addProductMode: 'add',
    }
  },

  methods:{
    confirmDialog,
    async getStockTakeDetails() {
      this.stockTakeProducts = await stockTakeService.getStockTakeDetails(this.$route.params.id, {
        page: this.detailsPage,
        per_page: this.detailsPerPage
      })
      this.loading = false
    },
    onDetailsPageChange(page){
      this.detailsPage = page;
      this.getStockTakeDetails();
    },
    onDetailsPerPageChange(perPage){
      this.detailsPerPage = perPage;
      this.detailsPage = 1;
      this.getStockTakeDetails();
    },
    onDetailsSearchChange(search){
      this.detailsSearch = search;
      this.detailsPage = 1;
      this.getStockTakeDetails();
    },
    populateAddProduct(storeProduct) {
      this.addProductModal = true
      this.addProductMode = 'add'
      this.selectedProduct.name = storeProduct.product.name
      this.stockTakeDetails.product_id = storeProduct.product_id
      this.stockTakeDetails.expected = storeProduct.stock
    },
    async addStockTakeProduct(e) {
      loadingButton(e)
      let response = await stockTakeService.createStockTakeDetails(this.stockTakeDetails)
      if (response) {
        this.addProductModal = false
        await this.getStockTakeDetails()
      }
      loadingButton(e, false)
    },
    async updateStockTakeProduct(e) {
      loadingButton(e)
      let response = await stockTakeService.updateStockTakeDetails(this.stockTakeDetails, this.stockTakeDetails.id)
      if (response) {
        this.addProductModal = false
        await this.getStockTakeDetails()
      }
      loadingButton(e, false)
    },
    populateStockTakeEdit(stockTake) {
      this.selectedProduct.name = stockTake.product.name
      this.stockTakeDetails.id = stockTake.id
      this.stockTakeDetails.product_id = stockTake.product_id
      this.stockTakeDetails.expected = stockTake.expected
      this.stockTakeDetails.available = stockTake.available
      this.stockTakeDetails.comment = stockTake.comment
      this.addProductModal = true
      this.addProductMode = 'edit'
    },
    async deleteStockTakeProduct(id) {
      let response = await stockTakeService.deleteStockTakeDetails(id)
      if (response) {
        await this.getStockTakeDetails()
      }
    },
    async submitStockTake() {
      await stockTakeService.approveStockTake(this.$route.params.id)
    },
  },

  computed: {
    additionalFields() {
      if (this.stockTake.status === 'pending') {
        return []
      }
      return [
        {
          label: 'Available',
          name: 'available',
        },
      ]
    },
  },

  created: async function () {
    this.stockTake = await stockTakeService.getStockTake(this.$route.params.id)
    await this.getStockTakeDetails()
  },

  watch: {
    addProductModal: function () {
      if (!this.addProductModal) {
        this.stockTakeDetails = {
          stock_take_id: this.$route.params.id
        }
      }
    }
  }

}
</script>

<style scoped>
</style>
